import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Steps,
  Progress,
  Tag,
  Button,
  Spin,
  Alert,
  List,
  Typography,
  Descriptions,
  Form,
  Input,
  Checkbox,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
  DownOutlined,
} from '@ant-design/icons';
import {
  OntologyApi,
  type OntologyValidationTask,
  type PublishValidationItem,
  type PublishValidationLevel,
  type MappingValidationDetails,
  type ProcessValidationDetails,
  type ValidationPhaseDescriptor,
} from '../api/ontologyApi';

const { Text } = Typography;

type Phase =
  | 'validating' // 正在创建/轮询校验任务
  | 'result' // 校验已完成，展示错误/警告/通过
  | 'form' // 填写版本信息
  | 'publishing' // 正在调用同步发布接口
  | 'success'; // 发布成功

interface PublishProgressModalProps {
  /** 本体 ID */
  ontologyId: number;
  /** 弹窗是否显示 */
  open: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 发布成功回调，用于刷新父组件数据 */
  onPublishComplete: () => void;
}

const levelConfig: Record<
  PublishValidationLevel,
  { color: string; icon: React.ReactNode; label: string }
> = {
  ERROR: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    label: '错误',
  },
  WARNING: {
    color: 'warning',
    icon: <ExclamationCircleOutlined />,
    label: '警告',
  },
  INFO: {
    color: 'default',
    icon: <InfoCircleOutlined />,
    label: '通过',
  },
};

/**
 * 根据校验任务状态判断 Steps 中某个阶段的 status。
 * phaseIndex 是该阶段在启用阶段列表中的 0-based 位置（后端 completedSteps 是按启用顺序递增的计数）。
 */
function getPhaseStepStatus(
  phase: ValidationPhaseDescriptor,
  phaseIndex: number,
  task: OntologyValidationTask | null
): 'wait' | 'process' | 'finish' | 'error' {
  if (!task) return 'wait';

  const results = task.validationResults || [];
  const phaseResults = results.filter(r => r.phaseOrder === phase.order);
  const completed = task.completedSteps ?? 0;

  if (phaseResults.length === 0) {
    if (completed >= phaseIndex + 1) return 'finish';
    if (task.status === 'RUNNING' && completed === phaseIndex) {
      return 'process';
    }
    return 'wait';
  }

  const hasError = phaseResults.some(r => r.level === 'ERROR');
  if (hasError) return 'error';
  if (completed >= phaseIndex + 1) return 'finish';
  return 'process';
}

/** 阶段2：数据映射详情 */
const MappingDetails: React.FC<{ details: MappingValidationDetails }> = ({
  details,
}) => (
  <div className="space-y-2">
    {details.sql && (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">执行 SQL</div>
        <pre className="text-xs bg-gray-100 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap break-all">
          {details.sql}
        </pre>
      </div>
    )}
    {details.data && Object.keys(details.data).length > 0 && (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">
          返回数据（第一行）
        </div>
        <Descriptions size="small" bordered column={1} className="text-xs">
          {Object.entries(details.data).map(([k, v]) => (
            <Descriptions.Item key={k} label={k}>
              {v === null || v === undefined ? (
                <span className="text-gray-400">null</span>
              ) : (
                String(v)
              )}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    )}
    {details.error && (
      <pre className="text-xs text-red-500 bg-red-50 rounded p-2 m-0 whitespace-pre-wrap break-all">
        {details.error}
      </pre>
    )}
  </div>
);

/** 阶段3：Process 执行详情 */
const ProcessDetails: React.FC<{ details: ProcessValidationDetails }> = ({
  details,
}) => (
  <div className="space-y-2">
    {details.inputData && Object.keys(details.inputData).length > 0 && (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">输入参数</div>
        <Descriptions size="small" bordered column={1} className="text-xs">
          {Object.entries(details.inputData).map(([k, v]) => (
            <Descriptions.Item key={k} label={k}>
              {v === null || v === undefined ? (
                <span className="text-gray-400">null</span>
              ) : typeof v === 'object' ? (
                JSON.stringify(v)
              ) : (
                String(v)
              )}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    )}
    {details.outputData && Object.keys(details.outputData).length > 0 && (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">输出数据</div>
        <Descriptions size="small" bordered column={1} className="text-xs">
          {Object.entries(details.outputData).map(([k, v]) => (
            <Descriptions.Item key={k} label={k}>
              {v === null || v === undefined ? (
                <span className="text-gray-400">null</span>
              ) : typeof v === 'object' ? (
                JSON.stringify(v)
              ) : (
                String(v)
              )}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    )}
    {details.userLogs && (
      <div>
        <div className="text-xs font-medium text-gray-500 mb-1">运行日志</div>
        <pre className="text-xs bg-gray-100 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap max-h-32">
          {details.userLogs}
        </pre>
      </div>
    )}
    {details.stderr && (
      <div>
        <div className="text-xs font-medium text-yellow-600 mb-1">stderr</div>
        <pre className="text-xs bg-yellow-50 text-yellow-800 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap max-h-32">
          {details.stderr}
        </pre>
      </div>
    )}
    {details.errorMessage && (
      <div>
        <div className="text-xs font-medium text-red-500 mb-1">错误信息</div>
        <pre className="text-xs bg-red-50 text-red-600 rounded p-2 m-0 whitespace-pre-wrap break-all">
          {details.errorMessage}
        </pre>
      </div>
    )}
    {details.executionTimeMs !== undefined && (
      <div className="text-xs text-gray-400">
        执行耗时：{details.executionTimeMs} ms
      </div>
    )}
  </div>
);

/** 将任意值格式化为字符串展示 */
const formatDetailValue = (v: unknown): React.ReactNode => {
  if (v === null || v === undefined) {
    return <span className="text-gray-400">null</span>;
  }
  if (typeof v === 'string') {
    return (
      <span className="whitespace-pre-wrap break-all">{v}</span>
    );
  }
  if (typeof v === 'number' || typeof v === 'boolean') {
    return String(v);
  }
  return (
    <pre className="text-xs bg-gray-100 rounded p-2 m-0 overflow-x-auto whitespace-pre-wrap break-all">
      {JSON.stringify(v, null, 2)}
    </pre>
  );
};

/** 通用 map 详情展示 */
const GenericDetails: React.FC<{ details: Record<string, unknown> }> = ({
  details,
}) => {
  const entries = Object.entries(details);
  if (entries.length === 0) return null;
  return (
    <Descriptions size="small" bordered column={1} className="text-xs">
      {entries.map(([k, v]) => (
        <Descriptions.Item key={k} label={k}>
          {formatDetailValue(v)}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );
};

/** 判断 details 是否有可展示内容 */
const hasRenderableDetails = (item: PublishValidationItem): boolean => {
  const d = item.details;
  if (!d) return false;
  if (typeof d !== 'object') return false;
  return Object.keys(d).length > 0;
};

/** 单项校验结果的可展开详情面板 */
const ItemDetails: React.FC<{ item: PublishValidationItem }> = ({ item }) => {
  if (!item.details) return null;
  if (item.phaseOrder === 2) {
    return <MappingDetails details={item.details as MappingValidationDetails} />;
  }
  if (item.phaseOrder === 3) {
    return <ProcessDetails details={item.details as ProcessValidationDetails} />;
  }
  return (
    <GenericDetails details={item.details as Record<string, unknown>} />
  );
};

/** 单条校验结果行，点击标题可展开详情 */
const ValidationResultRow: React.FC<{ item: PublishValidationItem }> = ({
  item,
}) => {
  const cfg = levelConfig[item.level];
  const hasDetails = hasRenderableDetails(item);
  const [expanded, setExpanded] = useState(false);

  const rowContent = (
    <div className="flex items-center gap-2 w-full py-1">
      <Tag color={cfg.color} icon={cfg.icon} className="shrink-0 !m-0">
        {cfg.label}
      </Tag>
      <Text className="text-xs text-gray-700 flex-1 min-w-0">
        {item.elementName ? (
          <span className="font-medium mr-1">[{item.elementName}]</span>
        ) : null}
        {item.message}
      </Text>
      {hasDetails && (
        <DownOutlined
          rotate={expanded ? 180 : 0}
          className="text-gray-400 text-xs shrink-0 ml-auto transition-transform"
        />
      )}
    </div>
  );

  return (
    <List.Item className="py-1 px-0" style={{ display: 'block' }}>
      {hasDetails ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setExpanded(v => !v)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setExpanded(v => !v);
            }
          }}
          className="cursor-pointer select-none"
        >
          {rowContent}
        </div>
      ) : (
        rowContent
      )}
      {hasDetails && expanded && (
        <div className="pl-1 pt-1">
          <ItemDetails item={item} />
        </div>
      )}
    </List.Item>
  );
};

/** 校验结果列表（单个阶段或全部） */
const ValidationResultList: React.FC<{
  items: PublishValidationItem[];
}> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <List
      size="small"
      dataSource={items}
      renderItem={item => <ValidationResultRow item={item} key={item.checkName} />}
    />
  );
};

/** 错误消息匹配辅助函数 */
const errorText = (e: any): string =>
  e?.message || e?.response?.data?.message || '';

const isConcurrentValidationError = (e: any) =>
  errorText(e).includes('本体校验任务进行中');

const isVersionNameExistsError = (e: any) =>
  errorText(e).includes('版本名称已存在');

const isDraftModifiedError = (e: any) =>
  errorText(e).includes('草稿在校验后已被修改');

const isWarningsNotIgnoredError = (e: any) =>
  errorText(e).includes('校验存在警告项');

const isValidationStaleError = (e: any) => {
  const msg = errorText(e);
  return (
    msg.includes('发布前须先完成校验') ||
    msg.includes('校验任务不存在')
  );
};

const PublishProgressModal: React.FC<PublishProgressModalProps> = ({
  ontologyId,
  open,
  onClose,
  onPublishComplete,
}) => {
  const [phase, setPhase] = useState<Phase>('validating');
  const [task, setTask] = useState<OntologyValidationTask | null>(null);
  const [ignoreWarnings, setIgnoreWarnings] = useState(false);
  const [publishErrorBanner, setPublishErrorBanner] = useState<string | null>(
    null
  );
  const [draftModified, setDraftModified] = useState(false);
  const [validationStale, setValidationStale] = useState(false);
  const [versionNameError, setVersionNameError] = useState<string | null>(null);
  /** 选中的阶段筛选；null 表示展示全部 */
  const [selectedPhaseOrder, setSelectedPhaseOrder] = useState<number | null>(
    null
  );
  const [versionForm] = Form.useForm();

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const currentTaskIdRef = useRef<number | null>(null);
  const ontologyIdRef = useRef(ontologyId);
  const onCloseRef = useRef(onClose);
  const onPublishCompleteRef = useRef(onPublishComplete);
  /** 标记 fetchTaskOnce 是否已将任务推进到终态，防止 startValidation 误重启轮询 */
  const validationCompletedRef = useRef(false);
  ontologyIdRef.current = ontologyId;
  onCloseRef.current = onClose;
  onPublishCompleteRef.current = onPublishComplete;

  const stopPolling = () => {
    if (pollingRef.current !== null) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  };

  const resetAllState = () => {
    stopPolling();
    currentTaskIdRef.current = null;
    setTask(null);
    setIgnoreWarnings(false);
    setPublishErrorBanner(null);
    setDraftModified(false);
    setValidationStale(false);
    setVersionNameError(null);
    setSelectedPhaseOrder(null);
    versionForm.resetFields();
  };

  /** 拉取一次任务详情，到达终态时停止轮询 */
  const fetchTaskOnce = async (tId: number) => {
    try {
      const data = await OntologyApi.getValidation(ontologyIdRef.current, tId);
      if (currentTaskIdRef.current !== tId) return;
      setTask(data);
      if (data.status === 'COMPLETED' || data.status === 'FAILED') {
        stopPolling();
        validationCompletedRef.current = true;
        // 仅在仍处于校验中阶段才切换到结果页，防止轮询延迟响应把用户从表单页拉回结果页
        setPhase(prev => (prev === 'validating' ? 'result' : prev));
      }
    } catch {
      // 拉取失败不中断轮询
    }
  };

  /** 启动轮询 */
  const startPolling = (tId: number) => {
    stopPolling();
    pollingRef.current = setInterval(() => {
      fetchTaskOnce(tId);
    }, 2000);
  };

  /** 启动一次校验任务（创建或复用后端已有任务），并开始轮询 */
  const startValidation = async () => {
    stopPolling();
    setTask(null);
    setPhase('validating');
    setDraftModified(false);
    setValidationStale(false);
    setPublishErrorBanner(null);
    validationCompletedRef.current = false;

    try {
      const taskId = await OntologyApi.createValidation(ontologyIdRef.current);
      currentTaskIdRef.current = taskId;
      await fetchTaskOnce(taskId);
      // 仅当 fetchTaskOnce 未将任务推进到终态时才启动轮询，
      // 否则轮询会再次拉取已完成的任务并可能把用户从表单页拉回结果页
      if (!validationCompletedRef.current) startPolling(taskId);
    } catch (err: any) {
      if (isConcurrentValidationError(err)) {
        // 已有进行中的校验任务，直接接管轮询
        try {
          const latest = await OntologyApi.getLatestValidation(
            ontologyIdRef.current
          );
          if (latest) {
            currentTaskIdRef.current = latest.id;
            setTask(latest);
            if (latest.status === 'RUNNING') {
              startPolling(latest.id);
            } else {
              setPhase('result');
            }
            return;
          }
        } catch {
          // fallthrough
        }
      }
      message.error(`启动校验失败：${errorText(err) || '未知错误'}`);
      onCloseRef.current();
    }
  };

  // 弹窗打开时启动校验；关闭时清理
  useEffect(() => {
    if (!open) {
      resetAllState();
      setPhase('validating');
      return;
    }
    resetAllState();
    setPhase('validating');
    startValidation();
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ontologyId]);

  // ---------- 用户动作 ----------

  /** 从结果页进入版本信息表单 */
  const handleProceedToForm = () => {
    if (!task || !task.publishable) return;
    // 存在警告时自动带上 ignoreWarnings=true
    setIgnoreWarnings(task.hasWarning === true);
    setPublishErrorBanner(null);
    setDraftModified(false);
    setValidationStale(false);
    setVersionNameError(null);
    versionForm.resetFields();
    setPhase('form');
  };

  /** 表单提交，调用同步发布接口 */
  const handleSubmitPublish = async () => {
    if (!task) return;
    let values: { versionName: string; versionDescription?: string };
    try {
      values = await versionForm.validateFields();
    } catch {
      return;
    }
    setPhase('publishing');
    setPublishErrorBanner(null);
    try {
      await OntologyApi.publishOntology(ontologyIdRef.current, {
        versionName: values.versionName,
        versionDescription: values.versionDescription,
        ignoreWarnings,
      });
      message.success('发布成功');
      onPublishCompleteRef.current();
      onCloseRef.current();
      return;
    } catch (err: any) {
      // 错误分类处理
      if (isVersionNameExistsError(err)) {
        setVersionNameError(errorText(err) || '版本名称已存在，请修改后重试');
        setPhase('form');
        return;
      }
      if (isDraftModifiedError(err)) {
        setPhase('form');
        setDraftModified(true);
        return;
      }
      if (isValidationStaleError(err)) {
        setPhase('form');
        setValidationStale(true);
        return;
      }
      if (isWarningsNotIgnoredError(err)) {
        setPhase('form');
        setIgnoreWarnings(true);
        setPublishErrorBanner('存在警告项，已自动勾选忽略，请重新提交');
        return;
      }
      setPhase('form');
      setPublishErrorBanner(errorText(err) || '发布失败，请稍后重试');
    }
  };

  /** 草稿被修改或校验失效：触发重新校验 */
  const handleRevalidate = () => {
    versionForm.resetFields();
    startValidation();
  };

  const handleClose = () => {
    stopPolling();
    onCloseRef.current();
  };

  // ---------- 渲染辅助 ----------

  const allResults = task?.validationResults || [];
  const errorItems = useMemo(
    () => allResults.filter(r => r.level === 'ERROR'),
    [allResults]
  );
  const warningItems = useMemo(
    () => allResults.filter(r => r.level === 'WARNING'),
    [allResults]
  );
  const infoItems = useMemo(
    () => allResults.filter(r => r.level === 'INFO'),
    [allResults]
  );

  /** 当前启用的校验阶段列表（由后端按配置动态返回） */
  const phases: ValidationPhaseDescriptor[] = task?.enabledPhases ?? [];

  const currentStep = task
    ? Math.min(task.completedSteps ?? 0, phases.length)
    : 0;

  /** 按阶段统计结果（key 为 phase.order） */
  const phaseStats = useMemo(() => {
    const stats: Record<
      number,
      { total: number; errors: number; warnings: number; infos: number }
    > = {};
    phases.forEach(p => {
      stats[p.order] = { total: 0, errors: 0, warnings: 0, infos: 0 };
    });
    allResults.forEach(r => {
      const s = stats[r.phaseOrder];
      if (!s) return;
      s.total++;
      if (r.level === 'ERROR') s.errors++;
      else if (r.level === 'WARNING') s.warnings++;
      else s.infos++;
    });
    return stats;
  }, [allResults, phases]);

  const stepsItems = phases.map((p, idx) => {
    const stepStatus = getPhaseStepStatus(p, idx, task);
    const s = phaseStats[p.order] ?? {
      total: 0,
      errors: 0,
      warnings: 0,
      infos: 0,
    };
    const isSelected = selectedPhaseOrder === p.order;

    let subTitle: React.ReactNode = null;
    if (s.errors > 0) {
      subTitle = <Tag color="error">{s.errors} 错误</Tag>;
    } else if (s.warnings > 0) {
      subTitle = <Tag color="warning">{s.warnings} 警告</Tag>;
    } else if (stepStatus === 'finish' && s.total > 0) {
      subTitle = <Tag color="success">通过</Tag>;
    } else if (stepStatus === 'finish') {
      subTitle = <Tag color="success">通过</Tag>;
    } else if (stepStatus === 'process') {
      subTitle = <Tag color="processing">进行中</Tag>;
    }

    return {
      title: (
        <span
          className={
            isSelected
              ? 'font-semibold text-blue-600'
              : 'text-gray-700'
          }
        >
          {p.phaseName}
        </span>
      ),
      subTitle,
      status: stepStatus,
    };
  });

  /** 当前列表应展示的结果（已应用阶段筛选） */
  const filteredResults = useMemo(() => {
    if (selectedPhaseOrder === null) return allResults;
    return allResults.filter(r => r.phaseOrder === selectedPhaseOrder);
  }, [allResults, selectedPhaseOrder]);

  const filteredErrorItems = filteredResults.filter(r => r.level === 'ERROR');
  const filteredWarningItems = filteredResults.filter(
    r => r.level === 'WARNING'
  );
  const filteredInfoItems = filteredResults.filter(r => r.level === 'INFO');

  /** 点击阶段步骤时切换筛选（按动态启用阶段的位置取 order） */
  const handleStepClick = (index: number) => {
    const target = phases[index];
    if (!target) return;
    const order = target.order;
    setSelectedPhaseOrder(prev => (prev === order ? null : order));
  };

  /** 弹窗标题 */
  const modalTitle = (() => {
    switch (phase) {
      case 'validating':
        return '发布本体 — 校验中…';
      case 'result':
        if (!task) return '发布本体';
        if (task.status === 'FAILED') return '校验执行异常';
        if (task.hasError) return '校验未通过，存在错误项';
        if (task.hasWarning) return '校验完成，存在警告项';
        return '校验通过';
      case 'form':
        return '填写版本信息';
      case 'publishing':
        return '正在发布…';
      case 'success':
        return '发布成功';
      default:
        return '发布本体';
    }
  })();

  /** footer 按钮 */
  const footerButtons = (() => {
    if (phase === 'validating') {
      return [
        <Button key="close" onClick={handleClose}>
          关闭
        </Button>,
      ];
    }

    if (phase === 'result') {
      if (!task) {
        return [
          <Button key="close" onClick={handleClose}>
            关闭
          </Button>,
        ];
      }
      // 校验失败或存在 ERROR：只能关闭去修复
      if (task.status === 'FAILED' || !task.publishable) {
        return [
          <Button key="close" onClick={handleClose}>
            关闭
          </Button>,
        ];
      }
      // 可发布：提供进入表单按钮
      return [
        <Button key="close" onClick={handleClose}>
          关闭
        </Button>,
        <Button key="to-form" type="primary" onClick={handleProceedToForm}>
          {task.hasWarning ? '忽略警告，发布' : '发布'}
        </Button>,
      ];
    }

    if (phase === 'form') {
      if (draftModified || validationStale) {
        return [
          <Button key="close" onClick={handleClose}>
            关闭
          </Button>,
          <Button key="revalidate" type="primary" onClick={handleRevalidate}>
            重新校验
          </Button>,
        ];
      }
      return [
        <Button key="close" onClick={handleClose}>
          取消
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmitPublish}>
          确认发布
        </Button>,
      ];
    }

    if (phase === 'publishing') {
      return []; // 发布中不提供按钮
    }

    if (phase === 'success') {
      return [
        <Button key="close" type="primary" onClick={handleClose}>
          完成
        </Button>,
      ];
    }

    return [
      <Button key="close" onClick={handleClose}>
        关闭
      </Button>,
    ];
  })();

  // ---------- 渲染各阶段 ----------

  const renderValidating = () => {
    if (!task) {
      return (
        <div className="flex items-center justify-center py-12">
          <Spin size="large" />
          <span className="ml-3 text-gray-500">正在启动校验…</span>
        </div>
      );
    }
    return (
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Text className="text-sm text-gray-500">
              {task.currentPhase || '准备中…'}
            </Text>
            <Text className="text-sm text-gray-400">
              {task.progressPercent}%
            </Text>
          </div>
          <Progress
            percent={task.progressPercent}
            status="active"
            size="small"
          />
        </div>
        <Steps
          size="small"
          current={currentStep}
          items={stepsItems}
          onChange={handleStepClick}
        />
        <div className="flex items-center text-gray-400 text-xs">
          <LoadingOutlined className="mr-1" />
          校验进行中，点击步骤可筛选对应阶段的检查项
        </div>
        {allResults.length > 0 && renderResultList()}
      </div>
    );
  };

  const renderResult = () => {
    if (!task) return null;
    const finishedPercent =
      task.status === 'COMPLETED' ? 100 : task.progressPercent;

    return (
      <div className="space-y-5">
        <div>
          <div className="flex items-center justify-between mb-1">
            <Text className="text-sm text-gray-500">
              {task.currentPhase || '校验完成'}
            </Text>
            <Text className="text-sm text-gray-400">{finishedPercent}%</Text>
          </div>
          <Progress
            percent={finishedPercent}
            status={
              task.status === 'FAILED'
                ? 'exception'
                : task.hasError
                  ? 'exception'
                  : 'success'
            }
            size="small"
          />
        </div>
        <Steps
          size="small"
          current={currentStep}
          items={stepsItems}
          onChange={handleStepClick}
        />

        {task.status === 'FAILED' && (
          <Alert
            type="error"
            icon={<CloseCircleOutlined />}
            showIcon
            message={task.errorMessage || '校验执行异常，请稍后重试'}
          />
        )}

        {task.status === 'COMPLETED' && task.hasError && (
          <Alert
            type="error"
            icon={<CloseCircleOutlined />}
            showIcon
            message={`校验发现 ${errorItems.length} 项错误，需修复后重新校验`}
          />
        )}

        {task.status === 'COMPLETED' &&
          !task.hasError &&
          task.hasWarning && (
            <Alert
              type="warning"
              icon={<ExclamationCircleOutlined />}
              showIcon
              message={`校验发现 ${warningItems.length} 项警告，确认后可忽略警告继续发布`}
            />
          )}

        {task.status === 'COMPLETED' &&
          !task.hasError &&
          !task.hasWarning && (
            <Alert
              type="success"
              icon={<CheckCircleOutlined />}
              showIcon
              message="全部校验通过，可以继续发布"
            />
          )}

        {allResults.length > 0 && renderResultList()}
      </div>
    );
  };

  const renderResultList = () => {
    const selectedPhase =
      selectedPhaseOrder !== null
        ? phases.find(p => p.order === selectedPhaseOrder)
        : null;

    return (
      <div
        className="border border-gray-200 rounded-lg overflow-hidden"
        style={{ maxHeight: 360, overflowY: 'auto' }}
      >
        <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
          <Text className="text-xs font-medium text-gray-600">
            {selectedPhase ? (
              <>
                {selectedPhase.phaseName}：{filteredResults.length} 项 ·{' '}
                {filteredErrorItems.length} 错误 ·{' '}
                {filteredWarningItems.length} 警告
              </>
            ) : (
              <>
                全部结果：{allResults.length} 项 · {errorItems.length} 错误 ·{' '}
                {warningItems.length} 警告
              </>
            )}
          </Text>
          {selectedPhase && (
            <Button
              type="link"
              size="small"
              onClick={() => setSelectedPhaseOrder(null)}
              className="!px-0 !h-auto"
            >
              展示全部
            </Button>
          )}
        </div>
        <div className="px-3 py-1">
          {filteredResults.length === 0 && (
            <div className="text-xs text-gray-400 py-3 text-center">
              该阶段暂无校验结果
            </div>
          )}
          {filteredErrorItems.length > 0 && (
            <>
              <div className="text-xs text-red-500 font-medium mt-2 mb-1">
                错误项（{filteredErrorItems.length}）
              </div>
              <ValidationResultList items={filteredErrorItems} />
            </>
          )}
          {filteredWarningItems.length > 0 && (
            <>
              <div className="text-xs text-yellow-600 font-medium mt-2 mb-1">
                警告项（{filteredWarningItems.length}）
              </div>
              <ValidationResultList items={filteredWarningItems} />
            </>
          )}
          {filteredInfoItems.length > 0 && (
            <>
              <div className="text-xs text-gray-400 font-medium mt-2 mb-1">
                通过项
              </div>
              <ValidationResultList items={filteredInfoItems} />
            </>
          )}
        </div>
      </div>
    );
  };

  const renderForm = () => (
    <div className="space-y-4">
      {draftModified && (
        <Alert
          type="warning"
          showIcon
          message="草稿已被修改"
          description="发布前需重新执行校验以保证结果与当前草稿一致。"
        />
      )}
      {validationStale && (
        <Alert
          type="warning"
          showIcon
          message="校验结果已失效"
          description="请重新执行校验后再发布。"
        />
      )}
      {!draftModified && !validationStale && task?.hasWarning && (
        <Alert
          type="warning"
          showIcon
          message={`校验存在 ${warningItems.length} 项警告，勾选忽略警告后可继续发布。`}
        />
      )}
      {publishErrorBanner && (
        <Alert type="error" showIcon message={publishErrorBanner} />
      )}
      <Form
        form={versionForm}
        layout="vertical"
        disabled={draftModified || validationStale}
        onValuesChange={changed => {
          if ('versionName' in changed && versionNameError) {
            setVersionNameError(null);
          }
        }}
      >
        <Form.Item
          label="版本名称"
          name="versionName"
          validateStatus={versionNameError ? 'error' : undefined}
          help={versionNameError || undefined}
          rules={[
            { required: true, message: '请输入版本名称' },
            { max: 50, message: '不超过50个字符' },
          ]}
        >
          <Input placeholder="如：v1.0.0" autoFocus />
        </Form.Item>
        <Form.Item
          label="版本描述"
          name="versionDescription"
          rules={[{ max: 200, message: '不超过200个字符' }]}
        >
          <Input.TextArea rows={3} placeholder="可选" />
        </Form.Item>
        {task?.hasWarning && (
          <Form.Item>
            <Checkbox
              checked={ignoreWarnings}
              onChange={e => setIgnoreWarnings(e.target.checked)}
            >
              我已知晓并忽略上述警告，继续发布
            </Checkbox>
          </Form.Item>
        )}
      </Form>
    </div>
  );

  const renderPublishing = () => (
    <div className="flex items-center justify-center py-12">
      <Spin size="large" />
      <span className="ml-3 text-gray-500">正在写入发布数据，请稍候…</span>
    </div>
  );

  const renderSuccess = () => (
    <div className="py-8">
      <Alert
        type="success"
        icon={<CheckCircleOutlined />}
        showIcon
        message="本体发布成功"
        description="新版本已生效，可在版本列表中查看。"
      />
    </div>
  );

  /** 禁止在表单验证/发布过程中通过确认发布按钮禁用（保留 UI 但在提交中禁止提交） */
  const submitDisabled =
    phase === 'form' &&
    !draftModified &&
    !validationStale &&
    task?.hasWarning === true &&
    !ignoreWarnings;

  // 覆盖表单态的"确认发布"按钮 disabled 状态
  const finalFooter = footerButtons.map(btn => {
    if (
      submitDisabled &&
      React.isValidElement(btn) &&
      btn.key === 'submit'
    ) {
      return React.cloneElement(btn as React.ReactElement<any>, {
        disabled: true,
      });
    }
    return btn;
  });

  return (
    <Modal
      title={modalTitle}
      open={open}
      onCancel={handleClose}
      footer={phase === 'publishing' ? null : finalFooter}
      width={900}
      maskClosable={false}
      closable={phase !== 'publishing'}
      destroyOnClose
    >
      {phase === 'validating' && renderValidating()}
      {phase === 'result' && renderResult()}
      {phase === 'form' && renderForm()}
      {phase === 'publishing' && renderPublishing()}
      {phase === 'success' && renderSuccess()}
    </Modal>
  );
};

export default PublishProgressModal;
